import type ReCAPTCHA from 'react-google-recaptcha';

import { addLogsToSentry } from './sentry';

/*
  Recaptcha flow
  * Pre requisites - recaptcha validation is enabled for only few accounts in devnet envs but for all accounts in prod
  1. Widget initialises based on where useRecaptchaToken used & gb flag enabled
  2. getFreshToken(executeAsync) is called the first time without resetting recaptcha
  3. Second time onwards in getFreshToken, reset recaptcha -> executeAsync is called
  4. Handles concurrent calls to getFreshToken with a serial queue & timeout mechanism
  5. Token gets invalidated as soon as backend send its for validation to google, so always new token is needed
  6. If for any reason recaptcha token throws invalid during BE validation, it gets catched through src/epics/auth.ts -> invalidCaptchaEpic
*/

const recaptchaRef: { current: ReCAPTCHA | null } = { current: null };

let isFirstTokenCall = true;

// Serial queue - each request waits for previous to complete
let queueTail: Promise<string | null> = Promise.resolve(null);

const TIMEOUT_MS = 10_000;
const QUEUE_TIMEOUT_MS = 2 * TIMEOUT_MS; // 20s - accounts for wait + execute

export const setRecaptchaRef = (r: ReCAPTCHA | null) => {
  recaptchaRef.current = r;
};

export const resetRecaptcha = () => {
  try {
    recaptchaRef.current?.reset();
  } catch (error: unknown) {
    addLogsToSentry(
      null,
      new Error('Recaptcha failed'),
      { extra: { error, source: 'resetHelper' } },
      { recaptchaError: 'reset failed' }
    );
  }
};

const executeAsyncInternal = async (): Promise<string | null> => {
  if (!recaptchaRef.current) {
    return null;
  }

  try {
    const token = await recaptchaRef.current.executeAsync();
    return token;
  } catch (error) {
    addLogsToSentry(
      null,
      new Error('Recaptcha failed'),
      { extra: { error, source: 'executeAsyncInternal' } },
      { recaptchaError: 'executeAsyncInternal failed' }
    );
    return null;
  }
};

const withTimeout = <T>(promise: Promise<T>, ms: number, fallback: T): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>(resolve => {
      setTimeout(() => resolve(fallback), ms);
    }),
  ]);
};

export const getFreshToken = async (): Promise<string | null> => {
  // Chain onto the queue - wait for previous request to complete (with timeout)
  const myTurn = withTimeout(queueTail, TIMEOUT_MS, null).then(async () => {
    // Reset before getting new token (except first call)
    if (!isFirstTokenCall) {
      resetRecaptcha();
    }
    isFirstTokenCall = false;

    // Race executeAsync with timeout
    const token = await withTimeout(executeAsyncInternal(), TIMEOUT_MS, null);
    return token ?? null;
  });

  // Wrap the entire operation with timeout before assigning to queueTail
  // Uses QUEUE_TIMEOUT_MS (2x) to account for both wait + execute timeouts
  // This ensures queueTail won't release the next caller while current operation is still in progress
  queueTail = withTimeout(myTurn, QUEUE_TIMEOUT_MS, null).catch(() => null);

  return myTurn;
};
